import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { applyInternalTrafficFromQuery, isInternalTraffic } from '@/utils/internalTraffic';

type Status = 'excluded' | 'restored' | 'blocked';

/**
 * /exclude-me — one-tap "stop counting this device in our analytics" page.
 *
 * Built for Pam (the founder's mother), who tests the marketing site LOGGED
 * OUT on her phone, so the email-based internal-traffic exclusion never
 * catches her. The mechanism already exists — `?internal=1` on /try and
 * /pass-plan sets a localStorage flag that tags every GA4 event
 * `traffic_type: internal` (see src/utils/internalTraffic.ts) — but a URL
 * param is not something a non-technical user remembers. This page IS the
 * URL: visiting it sets the same flag with no query string required, no
 * login required, and no button to press beyond loading the page.
 *
 * Reuses `applyInternalTrafficFromQuery()` (passing an explicit `?internal=1`
 * / `?internal=0`) rather than touching localStorage directly, so this page
 * can never drift from the flag logic every other internal-traffic entry
 * point already relies on.
 */
const ExcludeDevice: React.FC = () => {
  const [status, setStatus] = useState<Status>('excluded');

  useEffect(() => {
    applyInternalTrafficFromQuery('?internal=1');
    setStatus(isInternalTraffic() ? 'excluded' : 'blocked');
  }, []);

  const handleUndo = () => {
    applyInternalTrafficFromQuery('?internal=0');
    setStatus(isInternalTraffic() ? 'blocked' : 'restored');
  };

  const handleExcludeAgain = () => {
    applyInternalTrafficFromQuery('?internal=1');
    setStatus(isInternalTraffic() ? 'excluded' : 'blocked');
  };

  return (
    <main className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <Helmet>
        <title>Exclude this device · Social Work Test Prep Academy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div
        className="max-w-md w-full bg-white rounded-3xl border-2 border-neutral-200 shadow-ledge p-6"
        role="status"
      >
        <div className="text-center">
          {status === 'excluded' && (
            <>
              <CheckCircle className="mx-auto h-14 w-14 text-green-600" aria-hidden="true" />
              <h1 className="mt-4 font-display text-xl font-semibold text-neutral-900">
                This device is now excluded from our analytics
              </h1>
              <p className="mt-2 text-neutral-600">
                You won&apos;t be counted in our visitor numbers on this phone or browser.
                Thanks for testing, Pam.
              </p>
              <button
                type="button"
                onClick={handleUndo}
                className="mt-6 text-sm font-bold text-neutral-500 hover:text-neutral-700 underline"
              >
                Undo (count this device again)
              </button>
            </>
          )}

          {status === 'restored' && (
            <>
              <XCircle className="mx-auto h-14 w-14 text-neutral-400" aria-hidden="true" />
              <h1 className="mt-4 font-display text-xl font-semibold text-neutral-900">
                This device is counted again
              </h1>
              <p className="mt-2 text-neutral-600">
                We removed the exclusion. Your visits here will show up in our numbers like
                any other visitor.
              </p>
              <button
                type="button"
                onClick={handleExcludeAgain}
                className="mt-6 w-full rounded-2xl bg-primary-600 px-4 py-3 font-bold text-white transition-colors hover:bg-primary-700"
              >
                Exclude this device again
              </button>
            </>
          )}

          {status === 'blocked' && (
            <>
              <AlertTriangle className="mx-auto h-14 w-14 text-amber-500" aria-hidden="true" />
              <h1 className="mt-4 font-display text-xl font-semibold text-neutral-900">
                We could not save this on your device
              </h1>
              <p className="mt-2 text-neutral-600">
                Your browser is blocking this, most likely private or incognito mode.
                Use the same phone in normal mode and open this page again.
              </p>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default ExcludeDevice;
